/*eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Prompt } from 'react-router'
import useDeepCompareEffect from 'use-deep-compare-effect';
import PropTypes from 'prop-types';
import axios from 'axios';
import stringSimilarity from 'string-similarity';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import AddExamSerie from '../AddExamSerie/AddExamSerie';
import Button from '../Button/Button';
import InputField from '../InputField/InputField';
import SelectOptions from '../SelectOptions/SelectOptions';

import LoadingImage from '../../assets/svgs/LoadingImage';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';
import calculateBmi from '../../utils/calculateBmi';
import formatDate from '../../utils/formatDate';
import onlyNumbers from '../../utils/onlyNumbers';
import toTitleCase from '../../utils/toTitleCase';
import { doctorShape } from '../../utils/propTypesShapes';
import { useIsMounted } from '../../utils/useIsMounted';
import { setUploadExams } from '../../store/actions/exams';

import EXAM_MODULES from '../../data/exam-modules';
import EXAM_TYPES from '../../data/exam-types';
import SEX_OPTIONS from '../../data/sex-options';

import { AddExamFormOrthacStyle } from './AddExamFormOrthacStyle';

const AddExamFormOrthac = ({
  addExam,
  doctors,
  examIndex,
  handleDeleteSerie,
  handleLoading,
  handleProgress,
  reload,
  inputFiles,
  setDicomWarning,
}) => {
  const dispatch = useDispatch();
  const institution = useSelector((state) => state.settings.institution);
  const isMounted = useIsMounted();
  const language = useSelector((state) => state.settings.language);
  const uploadExams = useSelector((state) => state.exams.uploadExams);

  const [addExamSeries, setAddExamSeries] = useState([]);
  const [doctor, setDoctor] = useState(0);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [examDate, setExamDate] = useState('');
  const [examModule, setExamModule] = useState(0);
  const [examModuleOptions, setExamModuleOptions] = useState([]);
  const [examType, setExamType] = useState(0);
  const [examTypeOptions, setExamTypeOptions] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [medicalRecord, setMedicalRecord] = useState('');
  const [newPatient, setNewPatient] = useState({});
  const [patient, setPatient] = useState(0);
  const [patientAge, setPatientAge] = useState('');
  const [patientBirthDate, setPatientBirthDate] = useState('');
  const [patientBodyHeight, setPatientBodyHeight] = useState(0);
  const [patientBodyWeight, setPatientBodyWeight] = useState(0);
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientSex, setPatientSex] = useState(0);
  const [patients, setPatients] = useState([]);
  const [seriesId, setSeriesId] = useState('');
  const [seriesInstanceUID, setSeriesInstanceUID] = useState('');
  const [seriesDescription, setSeriesDescription] = useState('');
  const [studyId, setStudyId] = useState('');
  const [sexOptions, setSexOptions] = useState([]);
  const [showLess, setShowLess] = useState(false);

  const getOptionList = useCallback(
    (options) => {
      const optionValues = Object.values(options);

      if (optionValues && optionValues.length) {
        const optionList = [
          {
            key: 0,
            value: '',
            text: ' ',
          },
        ];

        optionValues.forEach((option) => {
          if (option && option.key && option.key.length) {
            optionList.push({
              key: option.key,
              value: option.key,
              text: translate(option.key.toLocaleLowerCase(), language),
            });
          }
        });

        if (optionList.length) {
          return optionList;
        }
      }

      return [];
    },
    [language]
  );

  const uptadeBirthDateByAge = (birthDate, age) => {
    const currentYear = new Date().getFullYear();
    const mmdd = birthDate.substring(4, 8);
    const currentPatientAgeInt = parseInt(age, 10);

    return `${currentYear - currentPatientAgeInt}${mmdd}`;
  };

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      if (addExam?.patientName?.length) {
        let currentPatients = [];

        if (!patients?.length) {
          currentPatients = await Api.searchPatientsByName(addExam.patientName);
        }

        if (currentPatients?.length) {
          setPatients([...currentPatients]);
        } else {
          setPatientOptions([
            {
              key: 0,
              value: 0,
              text: toTitleCase(addExam.patientName),
            },
          ]);
        }
      }
    })();
  }, [addExam.patientName, isMounted, patients?.length]);

  useEffect(() => {
    setExamTypeOptions([...getOptionList(EXAM_TYPES, language)]);
    setExamModuleOptions([...getOptionList(EXAM_MODULES, language)]);
    setSexOptions([...getOptionList(SEX_OPTIONS, language)]);
  }, [getOptionList, language]);

  useEffect(() => {
    const currentDoctorOptions = [{ key: 0, value: '', text: '' }];

    doctors.forEach((d) => {
      if (d && d.id && d.name && d.name.length) {
        currentDoctorOptions.push({ key: d.id, value: d.id, text: d.name });
      }
    });

    setDoctorOptions([...currentDoctorOptions]);
  }, [doctors]);

  useEffect(() => {
    const currentPatientOptions = [];

    patients.forEach((p) => {
      if (p?.name?.length) {
        currentPatientOptions.push({ key: p.id, value: p.id, text: p.name });
      }
    });

    setPatientOptions([...currentPatientOptions]);

    if (patients?.length === 1 && patients[0]) {
      setPatient(patients[0]?.id);
    }
  }, [patients]);

  useEffect(() => {
    if (addExam) {
      const examModuleNames = Object.keys(EXAM_MODULES);

      if (
        addExam.bodyPartExamined &&
        addExam.bodyPartExamined.length &&
        examModuleNames.length
      ) {
        const similarityModule = stringSimilarity.findBestMatch(
          addExam.bodyPartExamined,
          examModuleNames
        );

        if (
          similarityModule.bestMatch &&
          similarityModule.bestMatch.rating &&
          similarityModule.bestMatch.target &&
          similarityModule.bestMatch.target.length &&
          examModuleOptions.length
        ) {
          const currentExamModule = examModuleOptions.find(
            (eMO) =>
              eMO.value.toLowerCase() ===
              similarityModule.bestMatch.target.toLowerCase()
          );

          if (
            currentExamModule &&
            currentExamModule.value &&
            currentExamModule.value.length
          ) {
            setExamModule(currentExamModule.value);
          }
        }
      }

      if (addExam.modality && addExam.modality.length) {
        setExamType(addExam.modality);
      }

      if (addExam.patientAge && addExam.patientAge.length) {
        let currentPatientAge = onlyNumbers(addExam.patientAge);

        if (currentPatientAge && currentPatientAge.length) {
          while (currentPatientAge.charAt(0) === '0') {
            currentPatientAge = currentPatientAge.substring(1);
          }

          if (addExam.patientBirthDate && addExam.patientBirthDate.length) {
            const currentPatientBirthdate = uptadeBirthDateByAge(
              addExam.patientBirthDate,
              currentPatientAge
            );

            setPatientBirthDate(currentPatientBirthdate);
          }

          setPatientAge(currentPatientAge);
        }
      }

      const sexOptionsNames = Object.keys(SEX_OPTIONS);

      if (
        addExam.patientSex &&
        addExam.patientSex.length &&
        sexOptionsNames.length
      ) {
        const currentPatientSex = sexOptionsNames.find(
          (sON) =>
            sON.toLowerCase() === addExam.patientSex.toLowerCase() ||
            sON.toLowerCase() === addExam.patientSex.charAt(0).toLowerCase()
        );

        if (currentPatientSex && currentPatientSex.length) {
          setPatientSex(currentPatientSex);
        }
      }

      if (
        addExam.studyDate &&
        addExam.studyDate.length &&
        addExam.studyDate.length === 8
      ) {
        setExamDate(formatDate(addExam.studyDate));
      }

      if (addExam.accessionNumber && addExam.accessionNumber.length) {
        setMedicalRecord(addExam.accessionNumber);
      }

      if(addExam.series[0]?.seriesDescription && addExam.series[0]?.seriesDescription.length) {
        setSeriesDescription(addExam.series[0]?.seriesDescription);
      }

      if (addExam.seriesId && addExam.seriesId.length) {
        setSeriesId(addExam.seriesId);
      }

      if (addExam?.seriesInstanceUID?.length) {
        setSeriesInstanceUID(addExam?.seriesInstanceUID);
      }

      if (addExam?.studyId?.length) {
        setStudyId(addExam?.studyId);
      }
    }
  }, [addExam, examModuleOptions]);

  useDeepCompareEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      if (reload && addExam?.series?.length) {
        const currentAddExamSeries = [];

        setLoadingSeries(true);

        addExam?.series?.forEach(async (serie) => {
          const dicomDicomJpegPromises = [];
          const currentSerie = {
            seriesInstanceUID: serie?.seriesInstanceUID || '',
            seriesDescription: serie?.seriesDescription || seriesDescription,
            seriesDate: serie?.seriesDate || addExam?.studyDate || '',
            uploaded: true,
            imagesCount: addExam?.sliceCount || 0,
            dicomImages: [],
            dicomImage: null,
          };

          const imagesList = serie?.images || [];

          if (imagesList.length) {
            imagesList.forEach((path) => {
              if (path && path.length) {
                dicomDicomJpegPromises.push(Api.getDicomJpegByPath(path));
              }
            });
          } else if (addExam?.image && addExam.image.length) {
            dicomDicomJpegPromises.push(Api.getDicomJpegByPath(addExam.image));
          }

          if (dicomDicomJpegPromises?.length) {
            await axios
              .all(dicomDicomJpegPromises)
              .then((response) => {
                response?.forEach((dicomJpeg) => {
                  const dicomJpegBlob = new Blob([dicomJpeg], {
                    type: 'image/png',
                  });

                  currentSerie.dicomImages.push({
                    id: uuidV4(),
                    jpegs: [
                      {
                        id: uuidV4(),
                        jpeg: URL.createObjectURL(dicomJpegBlob),
                      },
                    ],
                  });
                });
              })
              .finally(() => {
                currentAddExamSeries.push({ ...currentSerie });

                setAddExamSeries(currentAddExamSeries);
              });
          }
        });

        setLoadingSeries(false);
      }
    })();
  }, [addExam, isMounted, reload]);

  const canCreateNewPatient = useMemo(
    () =>
      addExam &&
      addExam.patientName &&
      addExam.patientName.length &&
      patientOptions &&
      patientOptions.length &&
      !patientOptions[0].key,
    [addExam, patientOptions]
  );

  useEffect(() => {
    // TODO: Informar ao usuário erro ao criar paciente
    if (canCreateNewPatient) {
      const currentNewPatient = {
        name: toTitleCase(addExam.patientName),
      };

      if (patientBirthDate && patientBirthDate.length === 8) {
        currentNewPatient.birthday = formatDate(patientBirthDate);
      } else if (
        addExam.patientBirthDate &&
        addExam.patientBirthDate.length === 8
      ) {
        currentNewPatient.birthday = formatDate(addExam.patientBirthDate);
      }

      if (patientSex && patientSex.length) {
        currentNewPatient.sex = patientSex;
      } else {
        currentNewPatient.sex = 'O';
      }

      if (patientBodyWeight && patientBodyWeight.length) {
        currentNewPatient.weight = parseFloat(patientBodyWeight);
      }

      if (patientBodyHeight && patientBodyHeight.length) {
        currentNewPatient.height = parseFloat(patientBodyHeight);
      }

      setNewPatient(currentNewPatient);
    }
  }, [
    addExam.patientBirthDate,
    addExam.patientName,
    canCreateNewPatient,
    patientBirthDate,
    patientBodyHeight,
    patientBodyWeight,
    patientSex,
  ]);

  const disableSubmitExam = useMemo(() => {
    return (
      !institution ||
      (institution && !institution.id) ||
      !examModule ||
      (examModule && !examModule.length) ||
      !examType ||
      (examType && !examType.length) ||
      !addExamSeries.length ||
      !seriesDescription ||
      (seriesDescription && !seriesDescription.length ||
      !medicalRecord ||
      (medicalRecord && !medicalRecord.length))
    );
  }, [
    addExam.series,
    addExamSeries.length,
    examModule,
    examType,
    institution,
    medicalRecord,
  ]);

  const patientInformations = useMemo(() => {
    const informations = [];

    if (addExam.patientName && addExam.patientName.length) {
      informations.push(...toTitleCase(addExam.patientName).split(' '));
    }

    if (addExam.patientSex && addExam.patientSex.length) {
      informations.push(translate(addExam.patientSex.toLowerCase(), language));
    }

    if (addExam.patientAge && addExam.patientAge.length) {
      informations.push(onlyNumbers(addExam.patientAge));
    }

    return informations.join('_');
  }, [addExam, language]);

  const handleChangeSelect = (value, options, setValue) => {
    const currentOption = options.find(
      (option) => `${option.value}` === `${value}`
    );

    if (currentOption) {
      setValue(currentOption.value);
    }
  };

  const onClickSubmitExam = async () => {
    let dicomFiles = [];
    if (!disableSubmitExam) {
      try {
        let orthancResponse;
        const currentDicomFiles = [];

        for (let i = 0; i < inputFiles.length; i += 1) {
          const dicom = inputFiles[i];

          if (!dicomFiles.find((dF) => dF.name === dicom.name)) {
            currentDicomFiles.push(dicom);
          }
        }
        if (currentDicomFiles.length) {
          dicomFiles = currentDicomFiles;
          
          if (dicomFiles && dicomFiles?.length) {
            // setLoading('uploadingDicomFilesToTheServer');
    
            const dicomFormData = new FormData();

            const totalFiles = dicomFiles.length;
            let currentFile = 0;

            dicomFiles?.forEach((dicom, index) => {
              dicomFormData.append(
                `dicom${index + 1}`,
                dicom,
                `Dicom from ${new Date().getFullYear()}-${new Date().getMonth()}-${new Date().getDate()} ${new Date().getHours()}-${new Date().getMinutes()}-${new Date().getSeconds()}`
              );

              currentFile += 1;
              setTimeout(() => {
                handleProgress((currentFile / totalFiles) * 70); 
              }, index * 10);

            });
    
            if (dicomFormData && [...dicomFormData].length) {
              handleLoading('uploadingDicomFilesToTheServer');

              orthancResponse = await Api.uploadDicomOrthancFromSeries(seriesInstanceUID, dicomFormData);
              handleProgress(80);
    
              if (!Array.isArray(orthancResponse) || !orthancResponse.length) {
                handleLoading('');
                handleProgress(0);
                console.error('Erro ao fazer upload DICOM:', orthancResponse);
                setDicomWarning({
                  type: 'error',
                  text: 'errorUploadingDicomFile',
                });
                return;
              }
            }
          }
        }

        handleLoading('registeringExam');

        const examToSubmit = {
          registry: medicalRecord,
          type: examType,
          module: examModule,
          institution: institution.id,
        };

        if (orthancResponse?.length && orthancResponse[0]?.series_id?.length) {
          examToSubmit.series_id = orthancResponse[0].series_id;
          examToSubmit.series_instance_uid = seriesInstanceUID;
          examToSubmit.series_description = seriesDescription;
        }

        console.log('Dados do exame para submissão:', examToSubmit);

        if (orthancResponse?.length && orthancResponse[0]?.study_id?.length) {
          examToSubmit.study_id = orthancResponse[0].study_id;
        }

        if (examDate) {
          examToSubmit.date_requested = examDate;
        }

        if (doctor) {
          examToSubmit.doctor = doctor;
        }

        if (patientBodyHeight && patientBodyHeight.length) {
          examToSubmit.patient_height = parseFloat(patientBodyHeight);
        }

        if (patientBodyWeight && patientBodyWeight.length) {
          examToSubmit.patient_weight = parseFloat(patientBodyWeight);
        }

        if (patientAge && patientAge.length) {
          examToSubmit.patient_age =
            patientAge.length > 2 ? `0${patientAge}Y` : `${patientAge}Y`;
        }

        const currentUploadExams = uploadExams?.exams?.filter(
          (uE) => uE.seriesInstanceUID !== addExam.seriesInstanceUID
        );

        if (patient) {
          examToSubmit.patient = patient;

          await Api.submitExam(examToSubmit)
            .then(() => {
              dispatch(setUploadExams([...currentUploadExams]));
              handleProgress(100);
              handleLoading('');
              handleProgress(0);
            })
            .catch((error) => {
              console.error('Erro ao registrar exame:', error);
              throw error;
            });
        } else {
          await Api.registerPatient(newPatient)
            .then((registeredPatient) => {
              if (registeredPatient && registeredPatient.id) {
                examToSubmit.patient = registeredPatient.id;

                return Api.submitExam(examToSubmit)
                  .then(() => {
                    dispatch(setUploadExams([...currentUploadExams]));
                    handleProgress(100);
                    handleLoading('');
                    handleProgress(0);
                  })
                  .catch((error) => {
                    console.error('Erro ao registrar exame:', error);
                    throw error;
                  });
              } else {
                console.error('Erro: paciente não foi registrado corretamente', registeredPatient);
                throw new Error('Paciente não foi registrado');
              }
            })
            .catch((error) => {
              console.error('Erro ao registrar paciente:', error);
              throw error;
            });
        }
      } catch (error) {
        console.error('Erro geral ao submeter exame:', error);
        handleLoading('');
        handleProgress(0);
        setDicomWarning({
          type: 'error',
          text: 'errorRegisteringExam',
        });
      }
    }
  };

  const onChangePatientBodyHeight = (height) => {
    let currentHeight = onlyNumbers(height);

    if (currentHeight && currentHeight.length) {
      while (currentHeight.charAt(0) === '0') {
        currentHeight = currentHeight.substring(1);
      }
    }

    if (currentHeight.length <= 3) {
      setPatientBodyHeight(currentHeight);
    }
  };

  const onChangePatientBodyWeight = (weight) => {
    let currentWeight = onlyNumbers(weight);

    if (currentWeight && currentWeight.length) {
      while (currentWeight.charAt(0) === '0') {
        currentWeight = currentWeight.substring(1);
      }
    }

    if (currentWeight.length > 1 && currentWeight.length <= 4) {
      const start = currentWeight.substring(0, currentWeight.length - 1);
      const end = currentWeight.substring(currentWeight.length - 1);

      setPatientBodyWeight(`${start}.${end}`);
    } else if (currentWeight.length <= 1) {
      setPatientBodyWeight(currentWeight);
    }
  };

  const handleChangeAge = (age) => {
    const currentPatientAge = onlyNumbers(age);

    if (currentPatientAge.length) {
      if (
        addExam &&
        addExam.patientBirthDate &&
        addExam.patientBirthDate.length
      ) {
        const currentPatientBirthDate = uptadeBirthDateByAge(
          addExam.patientBirthDate,
          currentPatientAge
        );

        setPatientBirthDate(currentPatientBirthDate);
      }
    }

    setPatientAge(currentPatientAge);
  };

  return (
    <AddExamFormOrthacStyle className={showLess ? 'show-less' : ''}>
      <Prompt
        when={Boolean(inputFiles.length)}
        message={translate('exitWithoutSaving', language)}
      />
      <header className="add-exam-form-header">
        <div className="add-exam-id">{examIndex}</div>
        <div className="add-exam-patient-name">{patientInformations}</div>
        <div className="add-exam-preview-status">
          <button
            className="view-more"
            type="button"
            onClick={() => setShowLess(!showLess)}
          >
            {translate(showLess ? 'seeMore' : 'seeLess', language)}
          </button>
        </div>
      </header>
      <div className="add-exam-form-main">
        <div className="form-content-column">
          <h3 className="content-title">
            {translate('examDescription', language)}
          </h3>
          {examTypeOptions && examTypeOptions.length ? (
            <div className="form-content-item">
              <p className="content-item-label">
                {translate('examType', language)} *
              </p>
              <div className="content-item-input">
                <SelectOptions
                  theme="soft-blue-outline"
                  options={examTypeOptions}
                  value={examType}
                  onChange={(value) =>
                    handleChangeSelect(value, examTypeOptions, setExamType)
                  }
                />
              </div>
            </div>
          ) : null}
          {examModuleOptions && examModuleOptions.length ? (
            <div className="form-content-item">
              <p className="content-item-label">
                {translate('bodyPart', language)} *
              </p>
              <div className="content-item-input">
                <SelectOptions
                  theme="soft-blue-outline"
                  options={examModuleOptions}
                  value={examModule}
                  onChange={(value) =>
                    handleChangeSelect(value, examModuleOptions, setExamModule)
                  }
                />
              </div>
            </div>
          ) : null}
          {doctorOptions && doctorOptions.length ? (
            <div className="form-content-item">
              <p className="content-item-label">
                {translate('medicalAssistant', language)}
              </p>
              <div className="content-item-input">
                <SelectOptions
                  theme="soft-blue-outline"
                  options={doctorOptions}
                  value={doctor}
                  onChange={(value) =>
                    handleChangeSelect(value, doctorOptions, setDoctor)
                  }
                />
              </div>
            </div>
          ) : null}
          <div className="form-content-item">
            <p className="content-item-label">
              {translate('medicalRecord', language)} *
            </p>
            <div className="content-item-input">
              <InputField
                type="text"
                inputName="medical-record"
                value={medicalRecord}
                noMarginBottom
                onChange={setMedicalRecord}
              />
            </div>
          </div>
          <div className="form-content-item">
            <p className="content-item-label">
              {translate('serieDescription', language)}
            </p>
            <div className="content-item-input">
              <InputField
                type="text"
                inputName="series-description"
                value={seriesDescription}
                noMarginBottom
                onChange={setSeriesDescription}
              />
            </div>
          </div>
          <div className="form-content-item">
            <p className="content-item-label">
              {translate('examDate', language)}
            </p>
            <div className="content-item-input">
              <InputField
                type="date"
                inputName="medical-record"
                value={examDate}
                noMarginBottom
                onChange={setExamDate}
              />
            </div>
          </div>
        </div>
        <div className="form-content-column">
          <h3 className="content-title">
            {translate('patientInformation', language)}
          </h3>
          {patientOptions && patientOptions.length ? (
            <div className="form-content-item">
              <p className="content-item-label">
                {translate('name', language)} *
              </p>
              <div className="content-item-input">
                <SelectOptions
                  theme="soft-blue-outline"
                  options={patientOptions}
                  value={patient}
                  onChange={(value) =>
                    handleChangeSelect(value, patientOptions, setPatient)
                  }
                />
              </div>
            </div>
          ) : null}
          <div className="form-content-item">
            <p className="content-item-label">{translate('sex', language)}</p>
            <div className="content-item-input">
              {sexOptions && sexOptions.length ? (
                <SelectOptions
                  theme="soft-blue-outline"
                  options={sexOptions}
                  value={patientSex}
                  onChange={(value) =>
                    handleChangeSelect(value, sexOptions, setPatientSex)
                  }
                />
              ) : null}
            </div>
          </div>
          <div className="form-content-item">
            <p className="content-item-label">{translate('age', language)}</p>
            <div className="content-item-input">
              <InputField
                type="text"
                inputName="patient-name"
                value={patientAge}
                noMarginBottom
                onChange={handleChangeAge}
              />
            </div>
          </div>
          <div className="form-content-row">
            <div className="form-content-item">
              <p className="content-item-label">
                {`${translate('bodyHeight', language)} (cm)`}
              </p>
              <div className="content-item-input">
                <InputField
                  type="text"
                  inputName="patient-name"
                  value={patientBodyHeight}
                  noMarginBottom
                  onChange={onChangePatientBodyHeight}
                />
              </div>
            </div>
            <div className="form-content-item">
              <p className="content-item-label">
                {`${translate('bodyWeight', language)} (Kg)`}
              </p>
              <div className="content-item-input">
                <InputField
                  type="text"
                  inputName="patient-name"
                  value={patientBodyWeight}
                  noMarginBottom
                  onChange={onChangePatientBodyWeight}
                />
              </div>
            </div>
          </div>
          <div className="form-content-row">
            <div className="form-content-item">
              <p className="content-item-label">
                {`${translate('bmi', language)} (Kg/m²)`}
              </p>
              <div className="content-item-input">
                <InputField
                  type="text"
                  inputName="patient-name"
                  value={calculateBmi(
                    parseInt(patientBodyHeight, 10),
                    parseInt(patientBodyWeight, 10)
                  )}
                  noMarginBottom
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {!loadingSeries && addExamSeries.length ? (
        <div className="add-exam-series-list" lenght={addExamSeries.length}>
          {[...addExamSeries].map((serie) => (
            <div
              key={serie?.seriesId || serie?.seriesInstanceUID}
              className="add-exam-serie-container"
            >
              <AddExamSerie
                serie={serie}
                handleDeleteSerie={() => handleDeleteSerie(addExam, serie)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="add-exam-series-list-loading">
          <span className="loading-wrapper">
            <LoadingImage />
          </span>
          <p className="series-list-loading-message">
            {`${translate('extractingImagesFromDicomFiles', language)}…`}
          </p>
        </div>
      )}
      <div className="add-exam-button-container">
        <div className="button-wrapper">
          <Button
            theme={disableSubmitExam ? 'disabled' : 'lime-green'}
            type="button"
            onClick={onClickSubmitExam}
            text={translate('register', language).toUpperCase()}
            disabled={disableSubmitExam}
          />
        </div>
      </div>
    </AddExamFormOrthacStyle>
  );
};

AddExamFormOrthac.propTypes = {
  addExam: PropTypes.shape({
    accessionNumber: PropTypes.string,
    bodyPartExamined: PropTypes.string,
    modality: PropTypes.string,
    patientAge: PropTypes.string,
    patientBirthDate: PropTypes.string,
    patientId: PropTypes.string,
    patientName: PropTypes.string,
    patientSex: PropTypes.string,
    series: PropTypes.arrayOf(
      PropTypes.shape({
        seriesDate: PropTypes.string,
        seriesDescription: PropTypes.string,
        seriesInstanceUID: PropTypes.string,
      })
    ),
    seriesId: PropTypes.string,
    studyDate: PropTypes.string,
    studyId: PropTypes.string,
    studyInstanceUID: PropTypes.string,
    seriesInstanceUID: PropTypes.string,
  }),
  doctors: PropTypes.arrayOf(doctorShape),
  examIndex: PropTypes.number.isRequired,
  handleDeleteSerie: PropTypes.func.isRequired,
  handleLoading: PropTypes.func.isRequired,
  handleProgress: PropTypes.func.isRequired,
  reload: PropTypes.string,
  inputFiles: PropTypes.arrayOf(
    PropTypes.instanceOf(File)
  ),
};

AddExamFormOrthac.defaultProps = {
  addExam: {
    accessionNumber: '',
    bodyPartExamined: '',
    modality: '',
    patientAge: '',
    patientBirthDate: '',
    patientId: '',
    patientName: '',
    patientSex: '',
    series: [],
    seriesId: '',
    studyDate: '',
    studyId: '',
    studyInstanceUID: '',
  },
  doctors: [],
  reload: '',
};

export default AddExamFormOrthac;
